import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { CompetencyLadderEditor } from '@/components/admin/evaluation-rubrics/competency-ladder-editor'
import {
  CriterionCardChrome,
  CriterionDefinitionField,
  CriterionLabelField,
  CriterionStableKeyField,
  type CriterionCardChromeProps,
} from '@/components/admin/evaluation-rubrics/criterion-card-chrome'
import { SkillChipField } from '@/components/skills/skill-chip-field'
import type { RubricCriterion } from '@/hooks/use-evaluation-rubrics-admin'
import {
  slugifyCriterionKey,
  STANDARD_ORDINAL_LEVELS,
  type LevelDetail,
  type OrdinalLevelDef,
} from '@/lib/rubric-publish-payload'

type RubricCompetencyCardProps = Omit<CriterionCardChromeProps, 'children' | 'footer'> & {
  onUpdate: (patch: Partial<RubricCriterion>) => void
  skillOptions: string[]
  /** full = name/definition + ladder; levels = ladder + skills first (canvas L2). */
  depth?: 'full' | 'levels'
}

export function RubricCompetencyCard({
  row,
  errors,
  onUpdate,
  skillOptions,
  depth = 'full',
  ...chromeProps
}: RubricCompetencyCardProps) {
  const [advancedOpen, setAdvancedOpen] = useState(false)
  const ordinalLevels =
    ((row.scale as { levels?: OrdinalLevelDef[] } | undefined)?.levels) ??
    STANDARD_ORDINAL_LEVELS.map((l) => ({ ...l }))
  const levelDetails = (row.levels as Record<string, LevelDetail> | undefined) ?? {}
  const selectedSkills = row.component_skills ?? []

  const ladder = (
    <CompetencyLadderEditor
      embedded
      levels={ordinalLevels}
      levelDetails={levelDetails}
      onChangeLevels={(levels) =>
        onUpdate({ scale: { ...(row.scale ?? {}), type: 'ordinal', levels } })
      }
      onChangeDetails={(details) => onUpdate({ levels: details })}
    />
  )

  const skills = (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <Label className="text-sm font-medium">Linked skills</Label>
        <Badge variant="secondary">{selectedSkills.length}</Badge>
      </div>
      <SkillChipField
        id={`competency-skills-${row.key}`}
        skills={selectedSkills}
        onAdd={(name) => {
          if (!selectedSkills.includes(name)) {
            onUpdate({ component_skills: [...selectedSkills, name] })
          }
        }}
        onRemove={(name) =>
          onUpdate({ component_skills: selectedSkills.filter((s) => s !== name) })
        }
        profileSkills={skillOptions}
        placeholder="Link catalog skills…"
        helperText="Skills that inform this focus area in analysis reports."
      />
    </div>
  )

  const advanced = (
    <div>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="h-auto px-0"
        onClick={() => setAdvancedOpen((o) => !o)}
      >
        {advancedOpen ? (
          <ChevronDown className="mr-1 h-4 w-4" aria-hidden />
        ) : (
          <ChevronRight className="mr-1 h-4 w-4" aria-hidden />
        )}
        Advanced
      </Button>
      {advancedOpen ? (
        <div className="pt-2">
          <CriterionStableKeyField row={row} error={errors?.key} onUpdate={onUpdate} />
        </div>
      ) : null}
    </div>
  )

  if (depth === 'levels') {
    return (
      <CriterionCardChrome row={row} errors={errors} {...chromeProps}>
        <div className="space-y-1">
          <p className="text-sm font-medium">{row.label || row.key}</p>
          {row.definition ? (
            <p className="text-sm text-muted-foreground">{row.definition}</p>
          ) : null}
        </div>
        {ladder}
        {skills}
        {advanced}
      </CriterionCardChrome>
    )
  }

  return (
    <CriterionCardChrome row={row} errors={errors} {...chromeProps}>
      <CriterionLabelField
        row={row}
        error={errors?.label}
        onUpdate={(patch) => {
          onUpdate(patch)
          if (patch.label?.trim()) {
            const autoKey = slugifyCriterionKey(patch.label, 'competency')
            if (!row.key || row.key.startsWith('focus_')) {
              onUpdate({ ...patch, key: autoKey })
            }
          }
        }}
      />
      <CriterionDefinitionField row={row} onUpdate={onUpdate} />
      {ladder}
      {skills}
      {advanced}
    </CriterionCardChrome>
  )
}
